/**
 * The shell: sign-in state, navigation, and which screen is showing.
 *
 * One rule governs everything below — **the server decides, this decides what
 * to render.** Nav items and buttons are hidden for roles that cannot use them
 * so nobody is invited to click something that answers 403, but every one of
 * those routes is guarded by `adminGuard(permission)` on the API. If this file
 * and `PERMISSIONS` ever disagree, the API wins and the operator sees its
 * message.
 *
 * That rule is also why the phone's bottom bar is *derived* rather than listed.
 * Five slots and twelve destinations means choosing, and choosing from the
 * hard-coded twelve would give an auditor a bar whose first tab answers 403.
 * `NAV` is filtered by permission first; the bar is then filled from what
 * survives, in `MOBILE_FIRST` order.
 */

import { useEffect, useState, type ReactElement } from 'react';
import { api, ApiError } from './api/client.ts';
import { can, clearToken, getToken } from './auth.ts';
import { useRoute, href, navigate, type Route } from './router.ts';
import { Login } from './screens/Login.tsx';
import { Dashboard } from './screens/Dashboard.tsx';
import { DepositQueue } from './screens/DepositQueue.tsx';
import { DepositDetail } from './screens/DepositDetail.tsx';
import { WithdrawalQueue, WithdrawalDetail } from './screens/Withdrawals.tsx';
import { Rounds, RoundDetail } from './screens/Rounds.tsx';
import { GameTypes } from './screens/GameTypes.tsx';
import { Destinations } from './screens/Destinations.tsx';
import { Agents } from './screens/Agents.tsx';
import { Posters } from './screens/Posters.tsx';
import { Admins } from './screens/Admins.tsx';
import { Reconcile } from './screens/Reconcile.tsx';
import { Players } from './screens/Players.tsx';
import { PlayerDetail } from './screens/PlayerDetail.tsx';
import { IpAllowlist } from './screens/IpAllowlist.tsx';
import { Inbox } from './screens/Inbox.tsx';
import { Spinner } from './components/ui.tsx';
import {
  AdminsIcon,
  CloseIcon,
  DashboardIcon,
  DepositIcon,
  DestinationsIcon,
  AgentsIcon,
  GameTypesIcon,
  InboxIcon,
  IpAllowlistIcon,
  MoreIcon,
  PlayersIcon,
  PostersIcon,
  ReconcileIcon,
  RoundsIcon,
  WithdrawalIcon,
} from './components/icons.tsx';
import type { AdminView, NotificationCounts } from './api/types.ts';

type Session = { state: 'checking' } | { state: 'out' } | { state: 'in'; admin: AdminView };

type NavItem = {
  route: Route;
  label: string;
  permission: string | string[];
  icon: () => ReactElement;
  /** Which count feeds this tab's badge, if any. */
  badge?: keyof NotificationCounts;
};

const NAV: NavItem[] = [
  { route: { name: 'dashboard' }, label: 'Dashboard', permission: ['ledger.read', 'media_asset.manage'], icon: DashboardIcon },
  { route: { name: 'deposits' }, label: 'Deposits', permission: 'deposit.review', icon: DepositIcon, badge: 'deposits' },
  { route: { name: 'withdrawals' }, label: 'Withdrawals', permission: 'withdrawal.review', icon: WithdrawalIcon, badge: 'withdrawals' },
  { route: { name: 'rounds' }, label: 'Rounds', permission: 'round.read', icon: RoundsIcon },
  { route: { name: 'players' }, label: 'Players', permission: 'user.read', icon: PlayersIcon },
  { route: { name: 'inbox' }, label: 'Inbox', permission: 'user.read', icon: InboxIcon, badge: 'inbox' },
  { route: { name: 'gameTypes' }, label: 'Game types', permission: 'round.read', icon: GameTypesIcon },
  { route: { name: 'destinations' }, label: 'Destinations', permission: 'deposit.review', icon: DestinationsIcon },
  { route: { name: 'agents' }, label: 'Agents', permission: 'ledger.read', icon: AgentsIcon },
  { route: { name: 'posters' }, label: 'Posters', permission: 'media_asset.manage', icon: PostersIcon },
  { route: { name: 'admins' }, label: 'Admins', permission: 'admin.create', icon: AdminsIcon },
  { route: { name: 'ipAllowlist' }, label: 'IP allowlist', permission: 'admin.ipAllowlist', icon: IpAllowlistIcon },
  { route: { name: 'reconcile' }, label: 'Reconcile', permission: 'reconcile.run', icon: ReconcileIcon },
];

/**
 * Which destinations earn one of the phone's four fixed slots, in order.
 *
 * The three badged queues first, because they are the only screens that can
 * *tell you they need you* — the reason to pick the console up at all — then
 * Players, which is what a support question turns into. Everything else is
 * configuration you do at a desk, and lives one tap away in More.
 */
const MOBILE_FIRST: Array<Route['name']> = ['deposits', 'withdrawals', 'inbox', 'players'];

const MOBILE_SLOTS = 4;

/** Does this route belong to this nav item? Kept as the original shell had it. */
function isCurrent(route: Route, item: NavItem): boolean {
  return route.name.startsWith(item.route.name.replace(/s$/, ''));
}

export function App() {
  const [session, setSession] = useState<Session>({ state: 'checking' });
  const [counts, setCounts] = useState<NotificationCounts | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const route = useRoute();

  /**
   * A stored token is not proof of a live session — it can have been revoked
   * from another machine (§7.6's revoke button exists for exactly that). So the
   * app asks `/me` on load rather than trusting what is in storage, and a
   * revoked admin lands on the login screen instead of on a console where every
   * action fails.
   */
  useEffect(() => {
    if (!getToken()) {
      setSession({ state: 'out' });
      return;
    }
    void api
      .me()
      .then((admin) => setSession({ state: 'in', admin }))
      .catch(() => {
        clearToken();
        setSession({ state: 'out' });
      });
  }, []);

  /**
   * Any 401 from anywhere returns the whole console to the login screen.
   *
   * Sessions expire on their own schedule, and without this a reviewer's next
   * click after an expiry produces an error banner on a screen that still looks
   * signed in — so they retry, and retry, before thinking to reload.
   */
  useEffect(() => {
    const onRejection = (event: PromiseRejectionEvent): void => {
      if (event.reason instanceof ApiError && event.reason.isAuthFailure) {
        setSession({ state: 'out' });
      }
    };
    window.addEventListener('unhandledrejection', onRejection);
    return () => window.removeEventListener('unhandledrejection', onRejection);
  }, []);

  /**
   * Nav badges: what is waiting, without opening each tab.
   *
   * Polled rather than pushed. The console has no socket of its own, and a
   * queue that is a minute stale is not a problem an operator can perceive —
   * they are reading a header, not watching a ticker.
   *
   * Re-fetched on every route change as well as on the timer, so the badge
   * clears as soon as you finish the queue rather than up to a poll later.
   * Failures are swallowed on purpose: a badge is an affordance, and a network
   * blip should not raise an error banner over the screen you are working on.
   */
  const signedIn = session.state === 'in';
  useEffect(() => {
    if (!signedIn) return;
    let live = true;
    const refresh = (): void => {
      void api
        .notificationCounts()
        .then((next) => {
          if (live) setCounts(next);
        })
        .catch(() => undefined);
    };
    refresh();
    const timer = window.setInterval(refresh, 30_000);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [signedIn, route]);

  // Navigating is what the sheet is *for*, so arriving somewhere closes it.
  useEffect(() => setMoreOpen(false), [route]);

  // Escape closes it, and the page behind it does not scroll while it is up —
  // a sheet you can scroll the page under reads as a rendering fault.
  useEffect(() => {
    if (!moreOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setMoreOpen(false);
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [moreOpen]);

  if (session.state === 'checking') {
    return (
      <div className="shell shell--bare">
        <Spinner label="Checking your session…" />
      </div>
    );
  }

  if (session.state === 'out') {
    return <Login onSignedIn={(admin) => setSession({ state: 'in', admin })} />;
  }

  const me = session.admin;

  async function signOut(): Promise<void> {
    // Revoke server-side first so the token is dead even if it was captured;
    // clearing local storage alone would leave a working credential in flight.
    // A failure here still signs out locally — the alternative is trapping
    // someone in a console because the network is down.
    await api.logout().catch(() => undefined);
    clearToken();
    setSession({ state: 'out' });
  }

  const visible = NAV.filter((item) =>
    Array.isArray(item.permission)
      ? item.permission.some((p) => can(me.role, p))
      : can(me.role, item.permission),
  );

  // Preferred order first, then whatever else this role can see, so a narrow
  // role still gets a full bar rather than gaps where its permissions stop.
  const primary = [
    ...MOBILE_FIRST.map((name) => visible.find((i) => i.route.name === name)).filter(
      (i): i is NavItem => i !== undefined,
    ),
    ...visible.filter((i) => !MOBILE_FIRST.includes(i.route.name)),
  ].slice(0, MOBILE_SLOTS);

  const rest = visible.filter((i) => !primary.includes(i));

  function badgeFor(item: NavItem): number | null {
    if (!item.badge || !counts) return null;
    const n = counts[item.badge];
    return n > 0 ? n : null;
  }

  return (
    <div className="shell">
      <header className="topbar">
        <span className="topbar__brand">Bingo admin</span>
        <nav className="topbar__nav">
          {visible.map((item) => (
            <a
              key={item.label}
              className={`topbar__link${isCurrent(route, item) ? ' topbar__link--on' : ''}`}
              href={href(item.route)}
            >
              {item.label}
              {badgeFor(item) !== null ? (
                <span className="topbar__badge">{badgeFor(item)}</span>
              ) : null}
            </a>
          ))}
        </nav>
        <span className="topbar__me">
          {me.username} <span className="muted">· {me.role}</span>
        </span>
        <button type="button" className="button button--ghost" onClick={() => void signOut()}>
          Sign out
        </button>
      </header>

      <main className="main">
        <Screen route={route} me={me} />
      </main>

      <nav className="bottomnav" aria-label="Sections">
        {primary.map((item) => {
          const on = isCurrent(route, item);
          const badge = badgeFor(item);
          return (
            <a
              key={item.label}
              className={`bottomnav__item${on ? ' bottomnav__item--on' : ''}`}
              href={href(item.route)}
              aria-current={on ? 'page' : undefined}
            >
              <span className="bottomnav__iconwrap">
                <item.icon />
                {badge !== null ? (
                  <span className="bottomnav__badge">
                    {badge}
                    {/* The number alone reads as decoration to a screen reader
                        sitting next to a bare destination name. */}
                    <span className="sr-only"> waiting</span>
                  </span>
                ) : null}
              </span>
              <span className="bottomnav__label">{item.label}</span>
            </a>
          );
        })}

        <button
          type="button"
          className={`bottomnav__item${moreOpen ? ' bottomnav__item--on' : ''}`}
          aria-expanded={moreOpen}
          onClick={() => setMoreOpen((open) => !open)}
        >
          <span className="bottomnav__iconwrap">
            <MoreIcon />
          </span>
          <span className="bottomnav__label">More</span>
        </button>
      </nav>

      {moreOpen ? (
        <div className="sheet" role="dialog" aria-modal="true" aria-label="More sections">
          <button
            type="button"
            className="sheet__scrim"
            aria-label="Close"
            onClick={() => setMoreOpen(false)}
          />
          <div className="sheet__panel">
            <div className="sheet__grip" />

            <div className="sheet__who">
              <span>
                <span className="sheet__name">{me.username}</span>{' '}
                <span className="muted">· {me.role}</span>
              </span>
              <button
                type="button"
                className="button button--ghost button--icon"
                aria-label="Close"
                onClick={() => setMoreOpen(false)}
              >
                <CloseIcon />
              </button>
            </div>

            {rest.length > 0 ? (
              <div className="sheet__links">
                {rest.map((item) => {
                  const on = isCurrent(route, item);
                  const badge = badgeFor(item);
                  return (
                    <a
                      key={item.label}
                      className={`sheet__link${on ? ' sheet__link--on' : ''}`}
                      href={href(item.route)}
                      aria-current={on ? 'page' : undefined}
                    >
                      <item.icon />
                      <span className="sheet__linktext">{item.label}</span>
                      {badge !== null ? <span className="topbar__badge">{badge}</span> : null}
                    </a>
                  );
                })}
              </div>
            ) : null}

            <div className="sheet__foot">
              <button
                type="button"
                className="button button--wide"
                onClick={() => void signOut()}
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Screen({ route, me }: { route: Route; me: AdminView }) {
  switch (route.name) {
    case 'dashboard':
      return <Dashboard me={me} />;
    case 'deposits':
      return <DepositQueue tab={route.tab} />;
    case 'deposit':
      return <DepositDetail id={route.id} me={me} />;
    case 'withdrawals':
      return <WithdrawalQueue tab={route.tab} />;
    case 'withdrawal':
      return <WithdrawalDetail id={route.id} me={me} />;
    case 'rounds':
      return route.slug === undefined ? <Rounds /> : <Rounds slug={route.slug} />;
    case 'round':
      return <RoundDetail id={route.id} me={me} />;
    case 'gameTypes':
      return route.slug === undefined ? (
        <GameTypes me={me} />
      ) : (
        <GameTypes me={me} slug={route.slug} />
      );
    case 'destinations':
      return <Destinations me={me} />;
    case 'agents':
      return <Agents me={me} />;
    case 'posters':
      return <Posters me={me} />;
    case 'admins':
      return <Admins me={me} />;
    case 'reconcile':
      return <Reconcile />;
    case 'players':
      return <Players tab={route.tab} />;
    case 'player':
      return <PlayerDetail id={route.id} me={me} />;
    case 'ipAllowlist':
      return <IpAllowlist />;
    case 'inbox':
      return <Inbox me={me} />;
    default:
      // `parseHash` already funnels anything unrecognised to the queue; this
      // exists so adding a Route variant without a screen is a type error.
      navigate({ name: 'deposits' });
      return null;
  }
}
